import React from 'react'
import {
  Dimensions,
  Image,
  ImageSourcePropType,
  ImageStyle,
  Platform,
  TextStyle,
  View,
  ViewStyle,
  TouchableOpacity,
} from 'react-native'
import { observer } from 'mobx-react-lite'
import { color } from '../../theme'
import { Skeleton } from '../skeleton'
import { Text } from '../text'
import { getStrongestWeakest, TopicPctCorrectSummary } from '@trivie/core'
import { Instance } from 'mobx-state-tree'
import LinearGradient from 'react-native-linear-gradient'
import { useTranslation } from 'react-i18next'
import { Card } from '../card'
import { AssignedTopic } from '@trivie/core/src/models/AssignedTopic'
import defaultBackdrop from '../../assets/images/backdrops/default-backdrop.png'

interface TopicStatsProps {
  stats: Array<Instance<typeof TopicPctCorrectSummary>> | null
  style?: ViewStyle
  loading?: boolean
  topics?: Array<Instance<typeof AssignedTopic>> | []
  onPress: (item: Instance<typeof TopicPctCorrectSummary>) => void
  /** App-local default image override (required on Android; the ui-package fallback does not resolve there). */
  defaultImage?: ImageSourcePropType
}

export const TopicStats = observer((props: TopicStatsProps) => {
  const { loading, stats, style, onPress, defaultImage } = props

  const { strongest, weakest } = getStrongestWeakest(stats ?? [])

  if (loading) {
    return <Skeleton style={LOADER} />
  }

  return (
    <>
      {weakest?.length > 0 && (
        <Card
          style={{
            ...CONTAINER,
            ...style,
            alignItems: 'flex-start',
            marginRight: Platform.OS === 'web' ? 18 : 0,
          }}
        >
          <Text style={HEADER_TEXT} variant="body2Emphasized" uppercase tx="Topic.weakestTopics" />
          {weakest
            .sort((a, b) => a.pct_correct - b.pct_correct)
            .map((topic: Instance<typeof TopicPctCorrectSummary>) => (
              <TopicItem topic={topic} defaultImage={defaultImage} onPress={() => onPress(topic)} />
            ))}
        </Card>
      )}

      {strongest?.length > 0 && (
        <Card style={{ ...CONTAINER, ...style, alignItems: 'flex-start' }}>
          <Text
            style={HEADER_TEXT}
            variant="body2Emphasized"
            uppercase
            tx="Topic.strongestTopics"
          />
          {strongest
            .sort((a, b) => b.pct_correct - a.pct_correct)
            .map((topic: Instance<typeof TopicPctCorrectSummary>) => (
              <TopicItem
                strongest
                topic={topic}
                defaultImage={defaultImage}
                onPress={() => onPress(topic)}
              />
            ))}
        </Card>
      )}
    </>
  )
})

interface TopicItemProps {
  topic: Instance<typeof TopicPctCorrectSummary>
  strongest?: boolean
  onPress?: () => void
  defaultImage?: ImageSourcePropType
}
const TopicItem = observer((props: TopicItemProps) => {
  const { topic, strongest, onPress, defaultImage } = props

  return (
    <TouchableOpacity
      // @ts-ignore
      dataSet={{ qat: `${strongest ? 'strongest' : 'weakest'}_topic_${topic.topic_id}` }}
      style={{ width: '100%' }}
      onPress={onPress}
    >
      <View
        style={{
          flexDirection: 'row',
          width: '100%',
          marginBottom: 25,
          //@ts-ignore
          cursor: 'pointer',
        }}
      >
        <View style={{ borderRadius: 6, alignSelf: 'center' }}>
          <Image
            source={topic.image_url ? { uri: topic.image_url } : defaultImage ?? defaultBackdrop}
            style={TOPIC_IMG}
          />
        </View>
        <View style={TOPIC_CONTAINER}>
          <Text
            numberOfLines={1}
            style={{ marginBottom: 5 }}
            text={topic.title}
            variant="body1Emphasized"
          />
          <ProgressGradient strongest={strongest} value={topic.pct_correct} />
        </View>
      </View>
    </TouchableOpacity>
  )
})

interface ProgressProps {
  strongest: boolean | undefined
  value: number
}
const ProgressGradient = observer((props: ProgressProps) => {
  const { strongest, value } = props

  const { t } = useTranslation()
  return (
    <View style={PROGRESS_ROOT}>
      <View style={PROGRESS_CONTAINER}>
        <View style={{ ...PROGRESS_METER, backgroundColor: strongest ? '#1FE08F20' : '#FF3E1320' }}>
          <LinearGradient
            useAngle
            angle={143.13}
            locations={[0, 1]}
            start={{ x: 0, y: 0 }}
            end={{ x: 1, y: 0 }}
            colors={strongest ? ['#2FEA9B', '#7FDD53'] : ['#FFBF1A', '#FF4080']}
            style={{ ...GRADIENT, width: `${value}%` }}
          />
        </View>
        <Text
          style={PCT_TEXT}
          variant="body2Emphasized"
          tx={`${Math.round(value).toFixed(0)}% ${t('Common.correct')}`}
        />
      </View>
    </View>
  )
})

const PCT_TEXT: TextStyle = { bottom: 3, paddingLeft: 9, opacity: 0.75 }
const GRADIENT: ViewStyle = { borderRadius: 100, height: 10 }
const TOPIC_CONTAINER: ViewStyle = { flexDirection: 'column', paddingLeft: 12, flex: 1 }
const PROGRESS_ROOT: ViewStyle = { width: '100%', flex: 1 }
const PROGRESS_CONTAINER: ViewStyle = { ...PROGRESS_ROOT, flexDirection: 'row' }
const PROGRESS_METER: ViewStyle = {
  height: 10,
  borderRadius: 100,
  width: '100%',
  flex: 1,
  flexDirection: 'row',
}
const CONTAINER: ViewStyle = {
  borderWidth: 2,
  borderColor: '#F3F2F1',
  backgroundColor: color.white,
  borderRadius: 20,
  padding: 20,
  marginBottom: 16,
  width: Platform.OS === 'web' ? '100%' : Dimensions.get('screen').width - 40,
  paddingBottom: 0,
}
const LOADER: ViewStyle = {
  height: 435,
  width: Platform.OS === 'web' ? '100%' : Dimensions.get('screen').width - 40,
  borderRadius: 20,
  marginBottom: 16,
}
const TOPIC_IMG: ImageStyle = {
  borderRadius: 6,
  width: 40,
  height: 32,
  aspectRatio: 5 / 2,
  resizeMode: 'cover',
}
const DEFAULT_IMG: ViewStyle = {
  borderRadius: 6,
  width: 40,
  height: 32,
}
const HEADER_TEXT: TextStyle = { marginBottom: 16, color: color.shade50 }
